import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { Close } from "@mui/icons-material";
import { IconButton } from "@mui/material";

// Helper to generate financial year options
const getFinancialYearOptions = () => {
  const years = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = currentDate.getMonth() >= 3 ? currentYear : currentYear - 1;
  const endYear = 2015;
  for (let y = startYear + 1; y >= endYear; y--) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
};

const getCurrentFinancialYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const startYear = currentDate.getMonth() >= 3 ? currentYear : currentYear - 1;
  return `${startYear}-${startYear + 1}`;
};

export default function AddRevenueModal({
  isOpen,
  onClose,
  schemeId,
  schemeName,
  defaultFinancialYear,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    revenue: "",
    financialYear: defaultFinancialYear || getCurrentFinancialYear(),
    remarks: "",
  });

  const financialYearOptions = getFinancialYearOptions();

  useEffect(() => {
    if (isOpen) {
      setFormData({
        revenue: "",
        financialYear: defaultFinancialYear || getCurrentFinancialYear(),
        remarks: "",
      });
    }
  }, [isOpen, defaultFinancialYear]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatCurrency = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedSchemeId = parseInt(schemeId, 10);
    if (!parsedSchemeId) {
      toast.error("Valid Scheme ID is required");
      return;
    }

    const revenueAmount = parseFloat(formData.revenue);
    if (isNaN(revenueAmount) || revenueAmount < 0) {
      toast.error("Please enter a valid revenue amount");
      return;
    }

    if (!formData.financialYear?.trim()) {
      toast.error("Please select or enter a financial year");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        schemeId: parsedSchemeId,
        revenue: revenueAmount,
        financialYear: formData.financialYear.trim(),
        remarks: formData.remarks?.trim() || "",
      };

      const response = await axios.post(`${server}/scheme/revenue/new`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success !== false) {
        toast.success(response.data?.message || "Revenue added successfully!");
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        toast.error(response.data?.message || "Failed to add revenue");
      }
    } catch (error) {
      console.error("Error adding revenue:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.title ||
        "Failed to add revenue. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                  ₹
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Add Revenue</h2>
                  <p className="text-xs text-gray-500 font-medium">
                    Scheme ID: <span className="font-semibold text-emerald-700">#{schemeId}</span>
                    {schemeName ? ` • ${schemeName.slice(0, 30)}${schemeName.length > 30 ? "..." : ""}` : ""}
                  </p>
                </div>
              </div>
              <IconButton onClick={onClose} size="small" className="hover:bg-gray-200">
                <Close />
              </IconButton>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Financial Year Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Financial Year <span className="text-red-500">*</span>
                </label>
                <select
                  name="financialYear"
                  value={formData.financialYear}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all outline-none text-gray-800 font-medium"
                >
                  <option value="">Select Financial Year</option>
                  {financialYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* Revenue Amount */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex justify-between items-center">
                  <span>
                    Revenue Amount (₹) <span className="text-red-500">*</span>
                  </span>
                  {formData.revenue && !isNaN(parseFloat(formData.revenue)) && (
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                      {formatCurrency(formData.revenue)}
                    </span>
                  )}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-500 font-bold">
                    ₹
                  </div>
                  <input
                    type="number"
                    name="revenue"
                    min="0"
                    step="any"
                    value={formData.revenue}
                    onChange={handleChange}
                    required
                    placeholder="Enter revenue amount (e.g. 50000)"
                    className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all outline-none text-gray-900 font-semibold"
                  />
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Remarks / Description
                </label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder="Enter remarks or details about this revenue (e.g., Shop lease collection, parking fees, quarterly user charges)..."
                  rows="3"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all outline-none resize-none text-gray-800"
                />
              </div>

              {/* Form Action Buttons */}
              <div className="pt-3 flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold shadow-md shadow-emerald-200 transition-all flex items-center justify-center ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    "Save Revenue"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
